import { generatePayslipPdf, updatePayslipStatus } from "@/api/payslips";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePayContext } from "@/context/PayContext";
import { toast } from "@/hooks/use-toast";
import { AggregatePayslip, ProcessingStatus } from "@/models/payslips.model";
import { months } from "@/utils/date-format";
import { ColumnDef } from "@tanstack/react-table";
import {
  ArrowUpDown,
  Download,
  FileText,
  MoreHorizontal,
  Pencil,
  Undo2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export const ViewPayslipPdfActionCell = ({ row }: { row: any }) => {
  const getPayslipBlob = async () => {
    try {
      // Get the raw blob from your API instead of just the URL
      const response = await generatePayslipPdf(
        row.original.payslipId,
        row.original.userDetails.name,
        row.original.payslipMonth,
        row.original.payslipYear
      );

      // If your API returns a blob URL
      if (typeof response === 'string' && response.startsWith('blob:')) {
        // Fetch the actual blob data
        const blobResponse = await fetch(response);
        if (!blobResponse.ok) {
          throw new Error('Failed to fetch blob data');
        }
        const blob = await blobResponse.blob();
        return { blob, blobUrl: response };
      }
      // If your API directly returns a blob-like object
      else if (
        response &&
        typeof response === 'object' &&
        typeof (response as any).size === 'number' &&
        typeof (response as any).type === 'string'
      ) {
        const blobUrl = URL.createObjectURL(response as Blob);
        return { blob: response as Blob, blobUrl };
      }
      // Handle unexpected response type
      else {
        throw new Error('Unexpected response format from generatePayslipPdf');
      }
    } catch (error) {
      console.error("Error getting payslip data", error);
      toast({
        title: "Error retrieving payslip",
        variant: "destructive",
        description: "Please check your internet connection and try again",
      });
      throw error;
    }
  };


  const viewPdf = async () => {
    try {
      const { blobUrl } = await getPayslipBlob();
      window.open(blobUrl, "_blank");
      // Don't revoke the URL immediately since the user may want to download
      // Keep it for a longer time
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000); // 1 minute
    } catch (error) {
      // Error already handled in getPayslipBlob
    }
  };
  return (
    <div className="flex gap-2">
      <Button onClick={viewPdf} variant="outline" title="View Payslip">
        <FileText className="mr-1 h-4 w-4" />
        <span>View</span>
      </Button>

    </div>
  );
};
export const columns: ColumnDef<AggregatePayslip>[] = [
  {
    id: "month",
    accessorFn: (row) => row.payslipMonth,
    cell: ({ row }) => `${months[row.original.payslipMonth - 1]}`,
    header: ({ column }) => {
      return (
        <div className="flex items-center gap-2">
          Month
          <button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </button>
        </div>
      );
    },
  },
  {
    id: "year",
    accessorFn: (row) => `${row.payslipYear}`,
    cell: ({ row }) => row.original.payslipYear,
    header: ({ column }) => {
      return (
        <div className="flex items-center gap-2">
          Year
          <button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </button>
        </div>
      );
    },
  },
  {
    header: "Gross Pay",
    cell: ({ row }) => row.original.grossPay,
  },
  {
    header: "Employee CPF",
    cell: ({ row }) => row.original.totalEmployeeCpfContribution,
  },
  {
    header: "Net Pay",
    cell: ({ row }) => row.original.netPay,
  },
  {
    header: "Other Items",
    cell: ({ row }) => row.original.otherItems,
  },
  {
    header: "Net Payable",
    cell: ({ row }) => row.original.netPayable,
  },
  {
    header: "Actions",
    cell: ({ row }) => <ViewPayslipPdfActionCell row={row} />,
  },
];
